import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { FideAutofillConflict } from '@/lib/fide-autofill';

// Story 098 — confirmation shown when a FIDE-ID lookup would overwrite fields
// the user already filled with different values. Empty fields fill silently;
// only the conflicts land here. Shared by the director card + the profile.
export function FideAutofillDialog({
  conflicts,
  i18nNamespace,
  onOverwrite,
  onCancel,
}: {
  conflicts: FideAutofillConflict[] | null;
  i18nNamespace: string;
  onOverwrite: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(i18nNamespace);
  const open = !!conflicts && conflicts.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="fide-autofill-dialog">
        <DialogHeader>
          <DialogTitle>{t('fide_autofill.conflict_title')}</DialogTitle>
          <DialogDescription>{t('fide_autofill.conflict_body')}</DialogDescription>
        </DialogHeader>

        <ul className="space-y-2">
          {(conflicts ?? []).map((c) => (
            <li
              key={c.field}
              data-testid={`fide-conflict-${c.field}`}
              className="rounded border bg-muted/30 px-3 py-2 text-sm"
            >
              <span className="font-medium">
                {t(`fide_autofill.field.${c.field}`)}
              </span>
              <div className="mt-1 flex items-center gap-2 text-muted-foreground">
                <span className="line-through">{c.current}</span>
                <span aria-hidden="true">→</span>
                <span className="font-medium text-foreground">{c.proposed}</span>
              </div>
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button
            variant="outline"
            data-testid="fide-autofill-keep-mine"
            onClick={onCancel}
          >
            {t('fide_autofill.keep_mine')}
          </Button>
          <Button data-testid="fide-autofill-overwrite" onClick={onOverwrite}>
            {t('fide_autofill.overwrite')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
